import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)]',
  secondary:
    'bg-[var(--color-surface-muted)] text-[var(--color-foreground)] hover:bg-[var(--color-surface-elevated)] border border-[var(--color-border)]',
  ghost:
    'text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]',
  danger: 'bg-[var(--color-danger)] text-white hover:opacity-90',
};

const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-base',
};

// Reset bg/border/text to neutral on disable so a faded "Save changes" doesn't
// read as a half-broken destructive action. Hover variants stay neutral too.
const disabledStyles =
  'disabled:cursor-not-allowed disabled:border-transparent ' +
  'disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-foreground-subtle)] ' +
  'disabled:hover:bg-[var(--color-surface-muted)] disabled:hover:text-[var(--color-foreground-subtle)] ' +
  'disabled:hover:opacity-100';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ className, variant = 'primary', size = 'md', type = 'button', ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
          variantStyles[variant],
          sizeStyles[size],
          disabledStyles,
          className,
        )}
        {...rest}
      />
    );
  },
);
Button.displayName = 'Button';
